import React, { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const navItems = [
  { label: "Home", href: "#hero" },
  { label: "Companies", href: "#companies" },
  { label: "Solutions", href: "#features" },
  { label: "Metrics", href: "#stats" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#footer" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("#hero");
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const onScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const offsets = navItems
        .map((item) => {
          const el = document.querySelector(item.href);
          if (!el) {
            return null;
          }

          const top = el.getBoundingClientRect().top + window.scrollY;
          return { href: item.href, top };
        })
        .filter(Boolean)
        .sort((a, b) => a.top - b.top);

      const current = offsets
        .slice()
        .reverse()
        .find((entry) => window.scrollY + 120 >= entry.top);

      if (current) {
        setActiveSection(current.href);
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    const target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setIsOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: reduceMotion ? 0 : -28, opacity: reduceMotion ? 1 : 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${
          isScrolled
            ? "border-amber-200/70 bg-white/90 shadow-sm backdrop-blur-xl"
            : "border-transparent bg-white/70 backdrop-blur-md"
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => scrollTo("#hero")}
            className="flex items-center gap-3"
          >
            <img src="/logo.png" alt="Smart IT Box" className="h-11" />
          </button>

          <nav className="hidden items-center gap-2 lg:flex">
            {navItems.map((item) => (
              <button
                key={item.href}
                type="button"
                onClick={() => scrollTo(item.href)}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  activeSection === item.href
                    ? "bg-amber-100 text-amber-900"
                    : "text-gray-700 hover:text-amber-700"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="hidden lg:block">
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5"
            >
              Get Started
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setIsOpen((prev) => !prev)}
            className="inline-flex rounded-xl border border-amber-200 bg-white p-2 text-gray-700 lg:hidden"
          >
            {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm lg:hidden"
              onClick={() => setIsOpen(false)}
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="fixed right-0 top-0 z-50 h-full w-80 border-l border-amber-100 bg-white p-6 lg:hidden"
            >
              <div className="mb-8 flex items-center justify-between">
                <img src="/logo.png" alt="Smart IT Box" className="h-9" />
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg border border-amber-100 p-2"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-2">
                {navItems.map((item) => (
                  <button
                    key={item.href}
                    type="button"
                    onClick={() => scrollTo(item.href)}
                    className="block w-full rounded-xl px-4 py-3 text-left text-base font-medium text-gray-700 hover:bg-amber-50 hover:text-amber-800"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate("/auth");
                }}
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-3 text-sm font-semibold text-white"
              >
                Get Started
                <ArrowRight className="h-4 w-4" />
              </button>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;